import { label } from "../review/aktionFelder"
import type { ActionItem, ActionPlan } from "@/components/review/types"

/**
 * Der Plan in einem Satz — für den kompakten „Vorschlag" im Mail-Programm
 * (Variante A, 14.10.2026). Gebaut aus dem, was schon im Plan steht; kein
 * KI-Aufruf. Was der Satz nennt, ist genau das, was der Knopf ausführt.
 */

const WOCHENTAG = ["So", "Mo", "Di", "Mi", "Do", "Fr", "Sa"]

export function terminText(start?: string | null, dauerMin?: number | null): string {
  if (!start) return ""
  const d = new Date(start)
  if (isNaN(d.getTime())) return ""
  const tag = `${WOCHENTAG[d.getDay()]} ${String(d.getDate()).padStart(2, "0")}.${String(d.getMonth() + 1).padStart(2, "0")}.`
  const uhr = (x: Date) => x.getMinutes() ? `${x.getHours()}:${String(x.getMinutes()).padStart(2, "0")}` : `${x.getHours()}`
  if (!dauerMin) return `${tag}, ${uhr(d)} Uhr`
  const ende = new Date(d.getTime() + dauerMin * 60_000)
  return `${tag}, ${uhr(d)}–${uhr(ende)} Uhr`
}

function aufzaehlen(teile: string[]): string {
  if (teile.length <= 1) return teile[0] || ""
  return `${teile.slice(0, -1).join(", ")} und ${teile[teile.length - 1]}`
}

export function vorschlagSatz(plan: ActionPlan, gewaehlt: ActionItem[], monteurName?: (partnerId: number | null) => string | undefined): string {
  const teile: string[] = []
  const typen = new Set(gewaehlt.map((a) => a.type))
  for (const a of gewaehlt) {
    switch (a.type) {
      case "kontakt_anlegen":
        teile.push(plan.contact?.id ? "mit dem bestehenden Kontakt verknüpfen" : "Kontakt anlegen")
        break
      case "projekt_anlegen": {
        const t = (plan.project as { title?: string } | undefined)?.title?.trim()
        teile.push(t ? `Vorgang „${t}“ anlegen` : "Vorgang anlegen")
        break
      }
      case "termin_buchen": {
        const wann = terminText(plan.slot?.start_datetime, plan.slot?.duration_minutes)
        const wer = monteurName?.(plan.slot?.partner_id ?? null)
        teile.push(`Termin${wann ? ` am ${wann}` : ""}${wer ? ` mit ${wer}` : ""} buchen`)
        break
      }
      case "aufgabe_anlegen": {
        const t = plan.monteur_task?.title?.trim()
        const fuer = (plan.monteur_task as { target_user_name?: string } | undefined)?.target_user_name
        teile.push(`Aufgabe${t ? ` „${t}“` : ""}${fuer ? ` für ${fuer}` : ""} anlegen`)
        break
      }
      // Planwege 26.10.2026 (#97/#85/#91): der Satz nennt, WAS passiert —
      // welcher Termin, welcher Status, wohin die Mail geht.
      case "termin_stornieren": {
        const p = a.params || {}
        const wann = terminText(String(p.start ?? "").replace(" ", "T"))
        const wer = String(p.monteur ?? "").trim()
        teile.push(`Termin${wann ? ` am ${wann}` : ""}${wer ? ` (${wer})` : ""} absagen`)
        break
      }
      case "projekt_status_setzen": {
        const name = String(a.params?.step_name ?? "").trim()
        teile.push(name ? `Vorgang auf „${name}“ setzen` : label(a.type))
        break
      }
      case "mail_weiterleiten": {
        const an = String(a.params?.to ?? "").trim()
        teile.push(an ? `an ${an} weiterleiten` : label(a.type))
        break
      }
      case "antwort_senden":
        // Steht zuletzt — „… buchen und antworten" liest sich wie ein Ablauf.
        break
      default:
        teile.push(label(a.type))
    }
  }
  if (typen.has("antwort_senden")) teile.push(teile.length ? "antworten" : "Antwort senden")
  const satz = aufzaehlen(teile)
  return satz ? satz.charAt(0).toUpperCase() + satz.slice(1) + "." : ""
}

/** Beschriftung des Hauptknopfs — sagt, was passiert, statt „3 Aktionen". */
export function hauptKnopfText(gewaehlt: ActionItem[]): string {
  const typen = new Set(gewaehlt.map((a) => a.type))
  if (typen.size === 0) return "Ausführen"
  if (typen.has("termin_buchen") && typen.has("antwort_senden")) return "Buchen und senden"
  if (typen.size === 1) {
    const t = [...typen][0]
    if (t === "antwort_senden") return "Antwort senden"
    if (t === "termin_buchen") return "Termin buchen"
    return label(t)
  }
  if (typen.has("antwort_senden")) return "Ausführen und senden"
  return `${typen.size} Schritte ausführen`
}

/** Die erste Zeile mit Inhalt nach der Anrede — als Vorschau des Antworttexts. */
export function antwortAuszug(body?: string | null): string {
  const zeilen = (body || "").split("\n").map((z) => z.trim()).filter(Boolean)
  const ohneAnrede = zeilen.filter((z) => !/^(guten tag|hallo|sehr geehrte|liebe|moin)\b/i.test(z))
  const z = ohneAnrede[0] || zeilen[0] || ""
  return z.length > 160 ? z.slice(0, 157) + "…" : z
}
